import useMutationWithTracing from '@aurora/shared-client/components/useMutationWithTracing';

import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import type {
  CommunityGuideLinesForLanguageQuery,
  CommunityGuideLinesForLanguageQueryVariables,
  CreateOrUpdateCommunityGuideLinesMutation,
  CreateOrUpdateCommunityGuideLinesMutationVariables,
  CreateOrUpdatePrivacyPolicyMutation,
  CreateOrUpdatePrivacyPolicyMutationVariables,
  CreateOrUpdateTermsOfServiceMutation,
  CreateOrUpdateTermsOfServiceMutationVariables,
  PrivacyPolicyForLanguageQuery,
  PrivacyPolicyForLanguageQueryVariables,
  TermsOfServiceForLanguageQuery,
  TermsOfServiceForLanguageQueryVariables
} from '../../types/graphql-types';
import communityGuideLinesForLanguage from './CommunityGuideLinesForLanguage.query.graphql';
import createOrUpdateCommunityGuideLinesMutation from './CreateOrUpdateCommunityGuideLines.mutation.graphql';
import createOrUpdatePrivacyPolicyMutation from './CreateOrUpdatePrivacyPolicy.mutation.graphql';
import createOrUpdateTermsOfServiceMutation from './CreateOrUpdateTermsOfService.mutation.graphql';
import privacyPolicyForLanguage from './PrivacyPolicyForLanguage.query.graphql';
import termsOfServiceForLanguage from './TermsOfServiceForLanguage.query.graphql';

export enum TermsOfServiceType {
  COMMUNITY_GUIDELINES = 'communityGuideLinesForLanguage',
  PRIVACY_POLICY = 'privacyPolicyForLanguage',
  TERMS_OF_SERVICE = 'termsOfServiceForLanguage'
}

type TermsOfServiceQuery =
  | TermsOfServiceForLanguageQuery
  | PrivacyPolicyForLanguageQuery
  | CommunityGuideLinesForLanguageQuery;
type TermsOfServiceQueryVariables =
  | TermsOfServiceForLanguageQueryVariables
  | PrivacyPolicyForLanguageQueryVariables
  | CommunityGuideLinesForLanguageQueryVariables;

const TermsOfServiceQueryMapping = {
  [TermsOfServiceType.TERMS_OF_SERVICE]: {
    query: termsOfServiceForLanguage,
    variables: null
  },
  [TermsOfServiceType.PRIVACY_POLICY]: {
    query: privacyPolicyForLanguage,
    variables: {
      policyType: 'INTERNAL'
    }
  },
  [TermsOfServiceType.COMMUNITY_GUIDELINES]: {
    query: communityGuideLinesForLanguage,
    variables: {
      communityGuideLineType: 'INTERNAL'
    }
  }
};

export type TermsOfServiceMutation =
  | CreateOrUpdateTermsOfServiceMutation
  | CreateOrUpdatePrivacyPolicyMutation
  | CreateOrUpdateCommunityGuideLinesMutation;
export type TermsOfServiceMutationVariables =
  | CreateOrUpdateTermsOfServiceMutationVariables
  | CreateOrUpdatePrivacyPolicyMutationVariables
  | CreateOrUpdateCommunityGuideLinesMutationVariables;

const TermsOfServiceMutationMapping = {
  [TermsOfServiceType.TERMS_OF_SERVICE]: {
    query: createOrUpdateTermsOfServiceMutation,
    variables: null
  },
  [TermsOfServiceType.PRIVACY_POLICY]: {
    query: createOrUpdatePrivacyPolicyMutation,
    variables: {
      policyType: 'INTERNAL'
    }
  },
  [TermsOfServiceType.COMMUNITY_GUIDELINES]: {
    query: createOrUpdateCommunityGuideLinesMutation,
    variables: {
      communityGuideLineType: 'INTERNAL'
    }
  }
};

/**
 * Wrapper around useQueryWithTracing
 * @param termsOfServiceType - The type of terms of service
 * @param languageCode - Language code content has to update
 * @param enableFallbackToDefaultLocale - enable fallback to default locale
 */
export const useQueryTermsOfServiceForLanguageWithTracing = (
  termsOfServiceType: TermsOfServiceType,
  languageCode: string,
  enableFallbackToDefaultLocale: boolean
) => {
  const queryMapping = TermsOfServiceQueryMapping[termsOfServiceType];
  if (!queryMapping) {
    throw new Error('Invalid or Undefined TermsOfServiceType');
  }
  const { query } = queryMapping;
  let { variables } = queryMapping;
  if (languageCode) {
    variables = variables ?? {};
    variables['locale'] = `${languageCode}`;
    variables['enableFallbackToDefaultLocale'] = enableFallbackToDefaultLocale;
  }
  return useQueryWithTracing<TermsOfServiceQuery, TermsOfServiceQueryVariables>(module, query, {
    variables
  });
};

/**
 * Wrapper around useMutationWithTracing
 * @param termsOfServiceType - The type of terms of service
 */
export const useMutationTermsOfServiceForLanguageWithTracing = (
  termsOfServiceType: TermsOfServiceType
) => {
  const queryMapping = TermsOfServiceMutationMapping[termsOfServiceType];
  if (!queryMapping) {
    throw new Error('Invalid or Undefined TermsOfServiceType');
  }
  const { query, variables } = queryMapping;
  return useMutationWithTracing<TermsOfServiceMutation, TermsOfServiceMutationVariables>(
    module,
    query,
    {
      variables
    }
  );
};
